/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\ServerQuickListModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the telephonyServers module.
----------------------------------------------------------------*/


import { Server } from 'lucide-react';
import { QuickSearchModal } from '../../../components/ui/QuickSearchModal';
import { useI18n } from '../../../hooks/useI18n';
import { TelephonyServer } from '../types';

interface Props {
    isOpen: boolean;
    quickListSearch: string;
    filteredQuickList: TelephonyServer[];
    currentItemId?: string | null;
    onClose: () => void;
    onSearchChange: (v: string) => void;
    onItemSelect: (server: TelephonyServer) => void;
}

export function ServerQuickListModal({ isOpen, quickListSearch, filteredQuickList, currentItemId, onClose, onSearchChange, onItemSelect }: Props) {
    const { t } = useI18n();

    return (
        <QuickSearchModal
            isOpen={isOpen}
            onClose={onClose}
            title={t('telephonyServers.quickListTitle')}
            items={filteredQuickList.filter((s): s is typeof s & { id: string } => !!s.id)}
            isLoadingInitial={false}
            isSearching={false}
            searchValue={quickListSearch}
            onSearchChange={onSearchChange}
            onItemSelect={(server) => onItemSelect(server as TelephonyServer)}
            currentItemId={currentItemId ?? null}
            renderItem={(server, isCurrent) => (
                <div className="flex items-center justify-between">
                    <div>
                        <p className="font-medium text-foreground">{server.name}</p>
                        <p className="text-sm text-muted-foreground">{server.host}:{server.port}</p>
                        {server.description && <p className="text-sm text-muted-foreground">{server.description}</p>}
                    </div>
                    {isCurrent && (
                        <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                            {t('common.current', 'Current')}
                        </span>
                    )}
                </div>
            )}
            searchPlaceholder={t('telephonyServers.quickListSearchPlaceholder')}
            emptyMessage={t('telephonyServers.quickListEmpty')}
            noResultsMessage={t('telephonyServers.quickListEmptySearch')}
            headerIcon={<Server className="h-5 w-5" />}
            minSearchChars={0}
        />
    );
}
